/**
 * AuthProvider - React context provider for authenticated session state.
 *
 * Manages the authenticated user object, session lifecycle (login/logout),
 * and bootstrap flow (checking for an existing access token on mount).
 *
 * Architecture note:
 * - AuthProvider wraps AppRouter in main.tsx (Phase 3.1 ADR-006)
 * - Downstream components consume state via useAuth() (never import AuthContext directly)
 * - The api-client.ts 401 interceptor dispatches 'signalsentry:auth:expired' events;
 *   AuthProvider listens for these to update the UI without a re-fetch
 *
 * Bootstrap flow:
 * 1. If access token in localStorage: fetch /v1/auth/me
 *    - Success: set user + status='authenticated'
 *    - Failure: clear tokens + set status='unauthenticated'
 * 2. If no access token: set status='unauthenticated' immediately
 *
 * Actor-type routing (Phase 3.1):
 * Route registration is claim-aware via src/router/build-routes.tsx.
 * AuthProvider exposes user (with actor_type + impersonating_as +
 * target_org) to AppRouter, which rebuilds the routes whenever the
 * routing-relevant claim shape flips. This provider makes no routing
 * decisions itself; it only manages auth state.
 */
import { createContext, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { authStorage, staffSidecar, IMPERSONATION_NO_REFRESH } from '@/lib/auth-storage';
import { redirectToLogin, tryRefreshAccessToken } from '@/lib/api-client';
import { loginRequest, logoutRequest, meRequest, refreshRequest } from './api';
import {
  startImpersonation as startImpersonationOrchestrator,
  exitImpersonation as exitImpersonationOrchestrator,
} from '@/features/impersonation';
import type { AuthContextValue, User } from './schemas';

/**
 * Fetch the current user (/v1/auth/me), transparently refreshing an expired
 * access token ONCE before giving up.
 *
 * The access token lives ~15 minutes; the refresh token lives 24 hours
 * (rotation-on-use). Bootstrapping with only an expired access token - e.g. a
 * tab backgrounded long enough that the browser discarded and reloaded it -
 * must NOT log the user out. We silently exchange the refresh token for a new
 * pair (persisting the rotated tokens) and retry /me. Only when the refresh
 * ALSO fails, or there is no usable refresh token (absent, or the
 * IMPERSONATION_NO_REFRESH sentinel, which is not a JWT), does this throw -
 * signalling a genuinely ended session.
 *
 * This mirrors the 401 -> silent-refresh -> retry behavior that api-client.ts
 * already gives every OTHER API call; the bootstrap /me deliberately uses the
 * raw meRequest (not apiFetch) to avoid an import cycle, so it needs its own
 * refresh step rather than inheriting api-client's.
 *
 * @returns The authenticated User.
 * @throws The original /me error when the session cannot be silently refreshed.
 */
async function fetchMeWithSilentRefresh(): Promise<User> {
  try {
    return await meRequest();
  } catch (initialError) {
    const refresh = authStorage.getRefresh();
    // No usable refresh token (absent, or the impersonation sentinel which is
    // not a JWT) -> cannot recover; surface the original /me failure.
    if (!refresh || refresh === IMPERSONATION_NO_REFRESH) throw initialError;
    // Exchange the refresh token (rotation-on-use), persist the new pair, and
    // retry /me exactly once with the fresh access token.
    const rotated = await refreshRequest(refresh);
    authStorage.set(rotated.access_token, rotated.refresh_token);
    return await meRequest();
  }
}

/**
 * The authentication context.
 *
 * Do not consume this directly outside the auth feature.
 * Use useAuth() instead (throws with a helpful message when used outside provider).
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

/** Props for the AuthProvider wrapper. */
interface AuthProviderProps {
  /** Child tree that gains access to auth context. */
  children: ReactNode;
}

/**
 * AuthProvider component.
 *
 * Wraps the application with authentication state. Must sit above any
 * component that calls useAuth().
 *
 * @param children - Component tree with access to auth context
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<'initializing' | 'authenticated' | 'unauthenticated'>(
    'initializing',
  );

  /**
   * Bootstrap effect: runs once on mount to check for an existing session.
   *
   * Phase 3 baseline: read access token; if present, fetch /v1/auth/me;
   * on success set user, on failure clear tokens and set unauthenticated.
   *
   * Phase 3.1 Plan 03 extensions (per ADR-006 Negative consequences):
   *   Six recovery scenarios cover stale and orphaned sidecar state from
   *   prior crashed sessions, mid-impersonation tab refresh, etc.:
   *
   *     (a) no active token + no sidecar
   *         -> clean unauthenticated state.
   *
   *     (b) active token + no sidecar + /me=200 (staff or client)
   *         -> standard auth state. No sidecar action required.
   *
   *     (c) active token + sidecar + /me=200 with impersonating_as=true
   *         -> impersonation is currently in progress; preserve sidecar
   *         so an exit later can swap back.
   *
   *     (d) active token + sidecar + /me=200 with impersonating_as=false
   *         -> sidecar is stale (a previous impersonation exited via a
   *         path that did not clear it). Drop the sidecar; preserve the
   *         active staff token.
   *
   *     (e) active token + sidecar + /me=401
   *         -> impersonation token expired (or staff token expired during
   *         impersonation window). Drop EVERYTHING and route to /login.
   *         The router's requireAuthLoader catches the next render.
   *
   *     (f) NEW (M1 mitigation): no active token + sidecar present
   *         -> orphan sidecar from a crashed mid-impersonation session.
   *         Clear the sidecar, route to /login.
   *
   * The cancelled flag guards against state updates after unmount
   * (StrictMode double-invoke, fast-refresh, etc.).
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token = authStorage.getAccess();
      const hasSidecar = staffSidecar.has();

      if (!token) {
        // Branches (a) and (f): no active token. If a sidecar is present
        // it is orphaned (cannot recover without knowing whether the
        // impersonation token was valid; safer to drop and re-login).
        if (hasSidecar) staffSidecar.clear();
        if (!cancelled) setStatus('unauthenticated');
        return;
      }

      try {
        // Silent-refresh wrapper: an expired 15-min access token is
        // transparently exchanged for a new pair via the 24h refresh token and
        // /me is retried, so an idle/backgrounded tab that later reloads stays
        // signed in instead of being kicked out. The refresh token is the real
        // session lifetime; only a genuinely dead session reaches the catch.
        const me = await fetchMeWithSilentRefresh();
        if (cancelled) return;

        // Branch (d): /me confirms NOT impersonating but a sidecar is
        // present. The sidecar is stale from an exit path that did not
        // clear it. Drop the sidecar; keep the active staff token.
        if (!me.impersonating_as && hasSidecar) staffSidecar.clear();

        // Branches (b) and (c): preserve state as returned by /me. The
        // sidecar (if any) is intentionally retained for branch (c) so
        // a future exit can swap back to the staff pair.
        setUser(me);
        setStatus('authenticated');
      } catch {
        // Branch (e): the session could not be restored even after a silent
        // refresh attempt (refresh token expired/invalid, or an impersonation
        // token expired). Clear EVERYTHING (active + sidecar), mark
        // unauthenticated, AND hard-redirect to /login?returnTo=<path>.
        //
        // The redirect matters: if the current URL is an /admin/* route, the
        // rebuilt public route table no longer registers it, so without the
        // redirect react-router falls through to the catch-all 404 instead of
        // the login screen. That was the reported "404 - Page Not Found after
        // the tab was idle" bug (an expired access token on reload).
        authStorage.clear();
        staffSidecar.clear();
        if (!cancelled) setStatus('unauthenticated');
        redirectToLogin();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Listen for 'signalsentry:auth:expired' events dispatched by api-client.ts
   * when a token refresh fails during an in-flight API call.
   *
   * This keeps the UI in sync without requiring the api-client to hold a
   * reference to the React state setters.
   */
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setStatus('unauthenticated');
    };

    window.addEventListener('signalsentry:auth:expired', onExpired);
    return () => {
      window.removeEventListener('signalsentry:auth:expired', onExpired);
    };
  }, []);

  /**
   * Proactive token refresh (belt-and-suspenders on top of the reactive 401
   * path). The access token lives ~15 minutes; the reactive refresh in
   * apiFetch / authedFetch / the WebSocket only fires AFTER a request 401s, so
   * an idle tab can briefly surface a failed call before recovering (the live
   * Site Detail proxy poll + WS were the most exposed - that was the reported
   * "401 after idle" cascade). This timer renews the token at ~80% of its
   * lifetime so the session stays warm with zero user activity, removing the
   * blip entirely.
   *
   *   - Runs only while authenticated; torn down on logout / expiry.
   *   - Reuses the shared, de-duped tryRefreshAccessToken (so it never races
   *     the reactive refreshers - they all share one in-flight POST).
   *   - Returns null during impersonation (the 1h-capped token is
   *     intentionally NOT auto-renewed) or on a dead session; in that case the
   *     loop simply stops and the reactive paths drive the eventual /login.
   *   - Reschedules off each freshly-issued token's own `exp`; falls back to a
   *     10-minute cadence when the token can't be decoded.
   */
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (typeof window === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    // Decode a JWT's `exp` (ms since epoch) WITHOUT verifying it (the server
    // is the verifier; we only need the expiry to time the refresh). Returns
    // null when the token is absent or unparseable.
    const readExpMs = (): number | null => {
      const tok = authStorage.getAccess();
      if (!tok) return null;
      const parts = tok.split('.');
      if (parts.length < 2) return null;
      try {
        const payload = JSON.parse(
          atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
        ) as { exp?: number };
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
      } catch {
        return null;
      }
    };

    const FALLBACK_MS = 10 * 60 * 1000; // 10 min when exp is unreadable
    const MIN_DELAY_MS = 30 * 1000; // never schedule tighter than 30s

    const schedule = () => {
      if (cancelled) return;
      const expMs = readExpMs();
      const remaining = expMs ? expMs - Date.now() : FALLBACK_MS;
      const delay = Math.max(MIN_DELAY_MS, Math.floor(remaining * 0.8));
      timer = setTimeout(async () => {
        const refreshed = await tryRefreshAccessToken();
        // Keep the loop alive only while refresh works. A null result means
        // impersonation (don't auto-renew) or a dead session (let a real
        // request's 401 drive the redirect) - either way, stop.
        if (refreshed) schedule();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [status]);

  /**
   * Log in with email + password credentials.
   *
   * On success: stores tokens in localStorage, fetches /v1/auth/me to get
   * the canonical user object, updates context state.
   *
   * On credential failure: throws LoginError with code='invalid_credentials'.
   * On rate-limit: throws LoginError with code='rate_limited' + retry_after.
   *
   * @param email - User's email address
   * @param password - User's plaintext password
   * @returns The authenticated User object
   * @throws LoginError on credential or rate-limit failure
   */
  const login = useCallback(async (email: string, password: string): Promise<User> => {
    const response = await loginRequest(email, password);
    authStorage.set(response.access_token, response.refresh_token);

    // Re-fetch /v1/auth/me to get the canonical user shape (includes org details
    // that may not be embedded in the login response depending on token vintage).
    const me = await meRequest();
    setUser(me);
    setStatus('authenticated');
    return me;
  }, []);

  /**
   * Log out the current user.
   *
   * Always clears local tokens and updates state, even if the server-side
   * revocation call fails (network error, etc.). The access token expires in
   * 15 minutes regardless; local cleanup is the critical path.
   */
  const logout = useCallback(async (): Promise<void> => {
    await logoutRequest();
    authStorage.clear();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  /**
   * Start an impersonation session for the given client org.
   *
   * Wraps the start-impersonation orchestrator (which performs the
   * token-swap dance via authStorage + staffSidecar) and updates React
   * state so AppRouter rebuilds with client routes; ImpersonationRedirect
   * (mounted in AppShell) handles the rising-edge navigation to /now.
   *
   * @param orgId - Target client org ID to view as
   * @returns The post-swap User with impersonating_as=true + target_org set
   */
  const startImpersonation = useCallback(async (orgId: string): Promise<User> => {
    const me = await startImpersonationOrchestrator(orgId);
    setUser(me);
    setStatus('authenticated');
    return me;
  }, []);

  /**
   * Exit the active impersonation session and restore staff state.
   *
   * Wraps the exit-impersonation orchestrator (which swaps active tokens
   * back to the staff pair from the sidecar) and updates React state so
   * AppRouter rebuilds with admin routes; ImpersonationRedirect handles
   * the falling-edge navigation to /admin/fleet.
   *
   * @returns The post-swap User (the original staff actor)
   */
  const exitImpersonation = useCallback(async (): Promise<User> => {
    const me = await exitImpersonationOrchestrator();
    setUser(me);
    setStatus('authenticated');
    return me;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        status,
        login,
        logout,
        startImpersonation,
        exitImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
